import React, { useContext, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimationModeContext from "../context/AnimationContext";

// Props that only make sense in FM mode (spring interactions, layout, drag).
// Stripped in css/none modes to avoid errors and unintended side-effects.
const STRIP_IN_NON_FM = new Set([
  "layout",
  "layoutId",
  "whileHover",
  "whileTap",
  "whileInView",
  "whileFocus",
  "whileDrag",
  "drag",
  "custom",
  "onAnimationComplete",
  "onAnimationStart",
  "dragConstraints",
  "dragElastic",
  "dragMomentum",
]);

function stripInteractionProps(props) {
  const clean = {};
  for (const key in props) {
    if (
      Object.prototype.hasOwnProperty.call(props, key) &&
      !STRIP_IN_NON_FM.has(key)
    ) {
      clean[key] = props[key];
    }
  }
  return clean;
}

// Variant states can define their own `transition` (e.g. spring stiffness/damping).
// In CSS mode we want every transition to be a tween so framer-motion delegates
// playback to the Web Animations API (WAAPI) and the browser compositor.
// Variant-level transitions take priority over the component-level `transition`
// prop, so we must strip them before setting our own.
function stripVariantTransitions(variants) {
  if (!variants) return variants;
  const result = {};
  for (const key in variants) {
    if (!Object.prototype.hasOwnProperty.call(variants, key)) continue;
    const state = variants[key];
    if (state && typeof state === "object" && !Array.isArray(state)) {
      // eslint-disable-next-line no-unused-vars
      const { transition: _omit, ...rest } = state;
      result[key] = rest;
    } else {
      result[key] = state;
    }
  }
  return result;
}

// CSS mode tween: approximates the feel of a spring without JS physics math.
// Using `type:"tween"` tells framer-motion to hand the animation to WAAPI,
// which runs on the compositor thread and doesn't block the main thread.
// opacity and transform are the two compositor-eligible properties, so they
// animate entirely on the GPU with no layout/paint cost.
const CSS_TWEEN = { type: "tween", duration: 0.38, ease: [0.16, 1, 0.3, 1] };

// Build a cached proxy component for any HTML/SVG tag on first access
const cache = {};

function createProxyComponent(tag) {
  const MotionTag = motion[tag];

  const Component = forwardRef((props, ref) => {
    const mode = useContext(AnimationModeContext);

    // ── Framer-Motion mode ──────────────────────────────────────────────────
    // Pass everything through unchanged; springs, layout, interactions all on.
    if (mode === "framer-motion") {
      return React.createElement(MotionTag, { ...props, ref });
    }

    const cleanProps = stripInteractionProps(props);

    // ── None mode ───────────────────────────────────────────────────────────
    // No animations at all. MotionConfig(reducedMotion="always") is active for
    // this mode (set in AnimationProvider), so all JS transitions are instant.
    // `initial={false}` skips framer-motion's initial-state inline styles so
    // elements render immediately at their animate target without a one-frame
    // flash at opacity:0 / translated position.
    // `delete exit` → instant unmount, no exit flash.
    if (mode === "none") {
      cleanProps.initial = false;
      delete cleanProps.exit;
      return React.createElement(MotionTag, { ...cleanProps, ref });
    }

    // ── CSS / Native-Motion mode ─────────────────────────────────────────────
    // GPU-composited animations via the Web Animations API (WAAPI).
    // framer-motion uses WAAPI (not a JS RAF loop) for tween/keyframe
    // animations on `transform` and `opacity`, delegating playback entirely
    // to the browser compositor — no main-thread work during animation.
    //
    // Strategy:
    //   1. Strip variant-level `transition` keys (they would override ours).
    //   2. Set CSS_TWEEN as the component transition → WAAPI tween for every
    //      state change (enter, exit, re-render).
    //   3. Keep initial / animate / exit / variants so framer-motion manages
    //      enter/exit timing normally (AnimatePresence delays unmount for exits).
    if (cleanProps.variants) {
      cleanProps.variants = stripVariantTransitions(cleanProps.variants);
    }
    cleanProps.transition = CSS_TWEEN;

    return React.createElement(MotionTag, { ...cleanProps, ref });
  });

  Component.displayName = `m.${tag}`;
  return Component;
}

// Proxy auto-creates and caches components for any tag (div, section, path…)
export const m = new Proxy(cache, {
  get(target, tag) {
    if (typeof tag !== "string") return undefined;
    if (!target[tag]) {
      target[tag] = createProxyComponent(tag);
    }
    return target[tag];
  },
});

// AnimatePresence wrapper — always renders the real AnimatePresence so the
// React element tree stays stable across mode switches (avoids full remount).
export const MAnimatePresence = ({ children, ...props }) => {
  return React.createElement(AnimatePresence, props, children);
};
MAnimatePresence.displayName = "MAnimatePresence";
